import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useRegisterSW } from "virtual:pwa-register/react";

const intervalMS = 5 * 60 * 1000;

function registerPeriodicSync(swUrl: string, r: ServiceWorkerRegistration) {
  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  setInterval(async () => {
    if ("onLine" in navigator && !navigator.onLine) return;
    if (document.visibilityState !== "visible") return;

    try {
      const resp = await fetch(swUrl, {
        cache: "no-store",
        headers: {
          "cache-control": "no-cache",
        },
      });

      if (resp.status === 200) await r.update();
    } catch {
      // Network failures are expected (e.g. right after iOS resumes a
      // backgrounded tab); the next tick will retry.
    }
  }, intervalMS);
}

export function AppUpdater() {
  const { t } = useTranslation();

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl, r) {
      if (!r) return;

      if (r.active?.state === "activated") {
        registerPeriodicSync(swUrl, r);
      } else if (r.installing) {
        r.installing.addEventListener("statechange", (e) => {
          const sw = e.target as ServiceWorker;
          if (sw.state === "activated") registerPeriodicSync(swUrl, r);
        });
      }
    },
  });

  useEffect(() => {
    if (!needRefresh) return;

    toast(t("app_updater.title"), {
      action: {
        label: t("app_updater.reload"),
        onClick: () => void updateServiceWorker(true),
      },
      duration: Infinity,
    });
  }, [updateServiceWorker, needRefresh, t]);

  return null;
}
